import { Component, Inject, Input, SimpleChanges } from "@angular/core";
import { Chart } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {roundOfMaxsize} from '../common/common'
import { REGISTRY_APPLICATION } from "../../../../../global/tokens";

@Component({
  selector: "app-age-diagnosis-chart",
  templateUrl: "./age-diagnosis-chart.component.html",
  styleUrls: ["./age-diagnosis-chart.component.scss"],
})
export class AgeDiagnosisChartComponent {
  @Input() dataInsight: any;
  public loading:boolean=true
  
  constructor(
    @Inject(REGISTRY_APPLICATION) public application: string,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if(this.application === 'Admin'){
        if (changes['dataInsight'] && changes['dataInsight'].firstChange) {
            document.fonts.ready.then(() => {
                this.createLineChart()
                this.loading=false
            }).catch((err) => {  
                // even if the font failed to load the chart will be render
                this.createLineChart()
                this.loading=false
            });
        }
    }else{
        if (changes['dataInsight'] && !changes['dataInsight'].firstChange) {
            document.fonts.ready.then(() => {
                this.createLineChart()
                this.loading=false
            }).catch((err) => {  
                // even if the font failed to load the chart will be render
                this.createLineChart()
                this.loading=false
            });
        }
    }

  }


  createLineChart() {
    const labels = [
        "<13 years old",
        "13-17 years old",
        "18-26 years old",
        "27-44 years old",
        "45-65 years old",
        ">65 years old",
    ];

    const Mobilelabels = [
        "<13",
        "13-17",
        "18-26",
        "27-44",
        "45-65",
        ">65",
    ];

    const values = this.dataInsight?.age_counts_array;
    const percentageValue = this.dataInsight?.diagnosis_data|| [];
    // Index value to match the highlihtedBox
    const indexValue = this.dataInsight?.index;

    // Calculate Max size
    let { roundedMaxSize, stepSize } = roundOfMaxsize(this.dataInsight?.total_count);

    const canvas: any = document.getElementById("ageDiagnosisChart");
    const ctx = canvas.getContext("2d");
    canvas.width = canvas.offsetWidth;
    canvas.height = 280;
    Chart.defaults.global.defaultFontFamily= 'Poppins, sans-serif'
    const chart = new Chart(ctx, {
        type: "line",
        plugins: [ChartDataLabels],
        data: {
            labels:window.innerWidth < 992 ? Mobilelabels :labels,
            datasets: [
                {
                    label: "Participants",
                    data: values,
                    backgroundColor: "rgba(81, 103, 163, 1)",
                    borderColor: "rgba(81, 103, 163, 1)",
                    borderWidth: 4,
                    fill: false,
                    pointBackgroundColor: "white",
                    pointBorderColor: "rgba(81, 103, 163, 1)",
                    pointBorderWidth: 2,
                    pointRadius: 5,
                    lineTension: 0,
                },
            ],
        },
        options: {
            tooltips: {
                backgroundColor: "white",
                titleFontColor: "#000",
                bodyFontColor: "#000",
                borderColor: "#ccc",
                borderWidth: 1,
                titleFontSize:window.innerWidth < 768 ? 12 : 16,
                bodyFontSize: window.innerWidth < 768 ? 12 : 15,
                callbacks: {
                    label: function (tooltipItem, data) {
                        const index = tooltipItem.index;
                        const value = data.datasets[0].data[index] as number;
                        const percentage = percentageValue[index] !== undefined ? `${percentageValue[index]}%` : '';
                        return `${value} (${percentage})`;
                    }
                },
            },
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                datalabels: {
                    display: true,
                    // align: "top",
                    align: function (context) {
                        const chart = context.chart;
                        const index = context.dataIndex;
                        const dataset = context.dataset;
                        const value = dataset.data[index];
                    
                        const yScale = (chart as any).scales['y-axis-0'];
                        const yPixel = yScale.getPixelForValue(value);
                        const topBoundary = chart.chartArea.top;
                        const threshold = chart.height * 0.10;
                    
                        // If label is too close to top, switch to bottom
                        if (yPixel < topBoundary + threshold) {
                            if (window.innerWidth < 768) {
                                return 'top'; // Mobile: flip to bottom if too close to top
                            }
                            return 'bottom'; // Desktop: flip to bottom
                        }
                        // Otherwise, use original logic
                        return window.innerWidth < 768 ? 'start' : 'top';
                    },
                    offset: (context) => {
                        const index = context.dataIndex;
                        if (window.innerWidth < 768) {
                          return index === indexValue ? -36 : -24;
                        }
                        return 10;
                      },
                    color: (context) => {
                      const index = context.dataIndex;
                        return index === indexValue
                            ? "rgba(81,103,163,1)"
                            : "rgba(81, 103, 163, 1)";
                    },
                    backgroundColor: (context) => {
                      const index = context.dataIndex;
                        return index === indexValue ? "#fff" : "#fff";
                    },
                    padding: (context) => {
                        const index = context.dataIndex;
                        const isMobile = window.innerWidth < 768;
                      
                        if (index === indexValue) {
                          return isMobile 
                            ? { top: 3, bottom: 2, left: 3, right: 3 } // mobile view padding
                            : { top: 6, bottom: 4, left: 6, right: 6 }; // desktop view padding
                        }
                        return 0;
                      },
                    borderColor: (context) => {
                        const index = context.dataIndex;
                      
                        if (index === indexValue) {
                          return window.innerWidth < 768
                            ? "rgba(153, 153, 153, 0.6)"
                            : "rgba(153, 153, 153, 0.5)";
                        }
                      
                        return null;
                      },
                      borderWidth: (context) => {
                        const index = context.dataIndex;
                      
                        if (index === indexValue) {
                          return window.innerWidth < 768 ? 2 : 3;
                        }
                      
                        return 0;
                      },
                    borderRadius:(context) => {
                      const index = context.dataIndex;
                      return index === indexValue ? 6 : null; 
                  },
                    font: {
                        size: window.innerWidth < 768 ? 11 : 17,
                        weight: 500,
                    },
                    formatter: (value, context) => {
                        const index = context.dataIndex;
                        const percentage = percentageValue[index] !== undefined ? `${percentageValue[index]}%` : '';
                        return percentage;
                      }
                },
            },
            legend: {
                display: false,
            },
            scales: {
                xAxes: [
                    {
                        ticks: {
                            beginAtZero: false,
                            padding: 21,
                            fontSize:window.innerWidth < 768 ? 11 : 16,
                        },
                        gridLines: {
                            display: true,
                            offsetGridLines: true,
                        },
                        scaleLabel: {
                            display: true,
                            labelString: "Age at diagnosis",
                            fontSize: window.innerWidth < 768 ? 14 : 18,
                            fontColor: "rgba(62,62,62,1)",
                            fontStyle:"500",
                            fontFamily:"Montserrat,sans-serif"
                        },
                        offset: true,
                    },
                ],
                yAxes: [
                    {
                        ticks: {
                            min: 0,
                            max: roundedMaxSize,
                            stepSize: stepSize,
                            fontSize: window.innerWidth < 768 ? 11 : 16,
                        },
                        gridLines: {
                            display: true,
                        },
                    },
                ],
            },
        },
    });

}
}
